// packages/web-ui/src/state/open-new-chat.ts
//
// The "Chat" entry points (activity-bar button, Ctrl+1) open a *new*
// conversation — never resurrecting the current one or leaving an empty
// placeholder tab in the bar.
//
// One dedup guard: if you're already sitting in a blank new chat (the on-screen
// conversation has no messages yet), clicking "Chat" keeps you there instead of
// spawning a second empty session. Emptiness is read from the LIVE chat
// transcript (`messagesById`) — NOT the sessions-list `messageCount`, which
// lags a just-sent message and would otherwise make us treat a conversation you
// just typed into as "empty" and reopen it instead of starting fresh.

import { useRootStore } from './store.js';
import { sessionsStoreSingleton } from './sessions-store-singleton.js';
import { navigateTo } from '../router/hash-router.js';

/**
 * If the on-screen tab is a chat showing a not-yet-used (0-message) session,
 * return that session id; otherwise `null`. Reads the live chat transcript so
 * a just-sent message counts immediately.
 */
export function activeBlankChatSessionId(): string | null {
  const root = useRootStore.getState();
  const activeTab = root.openTabs.find((t) => t.id === root.activeTabId) ?? null;
  const activeId = root.activeSessionId;
  if (
    activeTab !== null &&
    activeTab.destination === 'chat' &&
    activeId !== null &&
    activeTab.resourceId === activeId &&
    (root.messagesById[activeId]?.length ?? -1) === 0
  ) {
    return activeId;
  }
  return null;
}

/**
 * Open a fresh chat conversation as an active session tab. Reuses the on-screen
 * session only when it's an already-blank new chat; otherwise creates a brand
 * new session. Navigating with a concrete `sessionId` opens/activates the real
 * `chat/<sessionId>` tab, so the user never sees the contentless bare-`chat`
 * placeholder.
 */
export async function openNewChat(): Promise<void> {
  const blankActive = activeBlankChatSessionId();
  if (blankActive !== null) {
    navigateTo({ kind: 'chat', sessionId: blankActive });
    return;
  }
  const id = await sessionsStoreSingleton.getState().createSession();
  if (id !== null) {
    navigateTo({ kind: 'chat', sessionId: id });
    return;
  }
  // Create failed — fall back to the bare `/chat` route; the auto-selector
  // surfaces its own retry affordance on error.
  navigateTo({ kind: 'chat' });
}
